<template>
    <v-container>
        <v-data-table
          :headers="headers"
          :items="trustTypes"
          :items-per-page.sync="getSettings.itemsperpage"
          :server-items-length="getSettings.serverItemsLength"
          :footer-props="getSettings.footerProps"
          @update:page="handlePage"
          dense
          class="elevation-1"
        >
        <template v-slot:top="{ pagination, options, updateOptions }">
          <v-toolbar flat class="mr-0 pr-0">
            <v-spacer />
            <div>
              <v-data-footer
                :pagination="pagination"
                :options="options"
                @update:options="updateOptions"
                v-bind="getSettings.footerProps"
                items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                :items-per-page.sync="getSettings.itemsperpage"
                :server-items-length="getSettings.serverItemsLength"
              />
            </div>
          </v-toolbar>
          <v-toolbar
            flat
          >
            <v-toolbar-title>Trust Types</v-toolbar-title>
            <v-divider
              class="mx-4" inset vertical
            ></v-divider>
            <v-text-field id="odTrustTypeFiscalYearText"
              class="odtrusttypes fiscalYear"
              v-model.number="criteria.fiscalYear"
              type="number"
              label="Fiscal Year"
              @input="criteriaChanged"
              ></v-text-field>
            <v-spacer></v-spacer>
            <!-- New/Edit item dialog -->
            <v-dialog
              v-model="dialog" max-width="70vw"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-if="canAdd"
                  class="mx-2" color="primary"
                  fab icon
                  v-bind="attrs" v-on="on"
                  @click="newItem"
                >
                  <v-icon>
                    {{icons.mdiPlus}}
                  </v-icon>
                </v-btn>
              </template>
              <v-card @keydown.enter="save">
                <v-card-title>
                  <span class="headline">{{ dialogTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col
                        cols="12" sm="6" md="3"
                      >
                        <v-text-field id="odTrustTypeListTrustTypeText"
                          class="od-trusttypelist-trusttype-text"
                          v-uppercase=globalSettingItem.conEverythingToCaps
                          v-model="editedItem.trustType"
                          label="Trust Type"
                          v-if="dialog" autofocus
                          :rules="reqdSelectValidation"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12" sm="6" md="5"
                      >
                        <v-text-field id="odTrustTypeListDescriptionText"
                          class="od-trusttypelist-description-text"
                          v-uppercase=globalSettingItem.conEverythingToCaps
                          v-model="editedItem.description"
                          label="Description"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12" sm="6" md="4"
                      >
                        <v-autocomplete id="odTrustTypeListDepartmentSelect"
                          class="od-trusttypelist-department-select"
                          v-model="editedItem.department"
                          :items="departments"
                          item-text="caption"
                          item-value="id"
                          label="OD Department"
                          :rules="reqdSelectValidation"
                        ></v-autocomplete>
                      </v-col>
                      <v-col
                        cols="12" sm="6" md="4"
                      >
                        <v-text-field id="odTrustTypeListFyText"
                          class="od-trusttypelist-fy-text"
                          v-model="editedItem.fiscalYear"
                          label="Fiscal Year"
                          readonly
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12" sm="6" md="4"
                      >
                        <v-text-field id="odTrustTypeListLastNumberText"
                          class="od-trusttypelist-lastnumber-text"
                          v-model="editedItem.lastNumberUsed"
                          label="Last Number Used"
                          readonly
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn id="odTrustTypeListCloseBtn"
                    class="od-trusttypelist-close-btn"
                    text color="blue darken-1"
                    @click="close"
                  >
                    Cancel
                  </v-btn>
                  <v-btn id="odTrustTypeListSaveBtn"
                    class="od-trusttypelist-save-btn"
                    text color="blue darken-1"
                    @click="save"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>

        <template v-slot:[`item.department`]="{ item }">
          <td class="text-start clickable" @click="editItem(item)">{{departmentCaption(item.department)}}</td>
        </template>
        <template v-slot:[`item.trustType`]="{ item, value }">
          <td class="text-start clickable" @click="editItem(item)">{{value}}</td>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon v-if="canDelete"
            small class="mr-2 od-browse-trusttypelist-delete-icon"
            @click="deleteItem(item)"
          >
            {{icons.mdiDelete}}
          </v-icon>
        </template>

        </v-data-table>
    </v-container>
</template>

<script>
import {
  mdiPlus,
  mdiDelete,
} from '@mdi/js';
import { mapState, mapGetters, mapActions } from 'vuex';
import {
  toFiscalYear,
} from '../../util/shared/tmc-global';
import GlobalToCaps from '../../util/shared/GlobalToCaps.mixin.vue';

const fields = {
  _id: '',
  trustType: '',
  description: '',
  department: undefined,
  fiscalYear: toFiscalYear(new Date()),
  lastNumberUsed: 0,
};

export default {
  name: 'TrustTypeList',
  mixins: [GlobalToCaps],
  props: {
    canAdd: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    icons: {
      mdiPlus,
      mdiDelete,
    },
    criteria: {
      fiscalYear: undefined,
      wildcard: undefined,
      skip: undefined,
      limit: undefined,
    },
    headers: [
      { text: 'Trust Type', value: 'trustType' },
      { text: 'Description', value: 'description' },
      { text: 'Department Link', value: 'department' },
      { text: 'Last Number Used', value: 'lastNumberUsed' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
    dialog: false,
    editedIndex: -1,
    editedItem: { ...fields },
  }),
  computed: {
    dialogTitle() {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item';
    },
    ...mapState({
      trustTypes: (state) => state.OD.trustTypes,
      departments: (state) => state.OD.departments,
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,
    }),
    ...mapGetters('user', [
      'getSettings',
    ]),
    reqdSelectValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
  },
  created() {
    this.criteria.fiscalYear = toFiscalYear(new Date());
    this.setCriteriaByPage(1);
    this.loadTrustTypes(this.criteria);
    this.loadDepartments({});
  },
  methods: {
    ...mapActions('OD', [
      'loadTrustTypes',
      'loadDepartments',
    ]),
    departmentCaption(departmentId) {
      const found = (this.departments || []).find((d) => d.id === departmentId);
      return found ? found.caption : '';
    },
    setCriteriaByPage(page) {
      const { itemsperpage } = this.getSettings;
      this.criteria.skip = (page - 1) * itemsperpage;
      this.criteria.limit = itemsperpage;
    },
    criteriaChanged() {
      this.loadTrustTypes(this.criteria);
    },
    handlePage(page) {
      this.setCriteriaByPage(page);
      this.loadTrustTypes(this.criteria);
    },
    defaultItem() {
      this.editedItem = { ...fields };
      this.editedIndex = -1;
      return this.editedItem;
    },
    newItem() {
      this.defaultItem();
      this.editedItem.fiscalYear = this.criteria.fiscalYear;
    },
    editItem(item) {
      this.editedIndex = this.trustTypes.indexOf(item);
      this.editedItem = { ...item };
      this.dialog = true;
    },
    deleteItem(item) {
      if (window.confirm('Are you sure you want to delete this item?')) {
        this.$store.dispatch('OD/deleteTrustType', item);
      }
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.defaultItem();
        this.dialog = false;
      });
    },
    save() {
      this.$store.dispatch('OD/upsTrustType', this.editedItem);
      this.close();
    },
  },
};
</script>

<style lang="sass">
.v-data-table
  > .v-data-footer
    margin-right: 21px
    > .v-data-footer__select
      margin-right: 18px
    > .v-data-footer__icons-before
      margin-right: 9px
    > .v-data-footer__icons-after
      margin-left: 4px
.text-start .clickable:hover
  cursor: pointer
  font-weight: 600
</style>
